import { z } from 'zod';
import { TIPOS_NOTIFICACAO } from './campanha.js';

/**
 * Contrato da fila de notificações do **painel interno** (SPEC §8 "Notificações",
 * UX-PAINEL-INTERNO §6). É a mesma fila `NotificacaoPendente` que o Hermes lê em
 * `/interno/notificacoes` — aqui com role `admin`/`atendente` e com a `origem`
 * já resolvida em texto legível (E8), não só os FKs.
 *
 * Nenhuma regra de negócio: só formato do contrato.
 */

export const STATUS_NOTIFICACAO = ['pendente', 'enviada', 'cancelada'] as const;
export type StatusNotificacao = (typeof STATUS_NOTIFICACAO)[number];

export const listarNotificacoesQuerySchema = z.object({
  /** sem `status` = todas as notificações (a fila do painel sempre passa `pendente`). */
  status: z.enum(STATUS_NOTIFICACAO).optional(),
});
export type ListarNotificacoesQuery = z.infer<typeof listarNotificacoesQuerySchema>;

/**
 * Origem legível de uma notificação (E8). Resolvida a partir de `indicacaoId` /
 * `movimentoBrindeId`. Para `boas_vindas` / `recuperacao_senha` não há FK de
 * origem — `tipo` fica `null` e `descricao` traz uma frase genérica.
 */
export const notificacaoOrigemSchema = z.object({
  tipo: z.enum(['ordem_servico', 'indicacao']).nullable(),
  numeroOs: z.string().nullable(),
  indicadorNome: z.string().nullable(),
  indicadorPrimeiroNome: z.string().nullable(),
  indicadoNome: z.string().nullable(),
  indicadoPrimeiroNome: z.string().nullable(),
  /** frase pronta para o operador, ex.: "indicação de Marina" ou "OS OS-1234". */
  descricao: z.string(),
});
export type NotificacaoOrigem = z.infer<typeof notificacaoOrigemSchema>;

export const notificacaoPainelSchema = z.object({
  id: z.string().uuid(),
  clienteId: z.string().uuid(),
  clienteNome: z.string(),
  clientePrimeiroNome: z.string(),
  telefone: z.string(),
  tipo: z.enum(TIPOS_NOTIFICACAO),
  texto: z.string(),
  linkWa: z.string(),
  status: z.enum(STATUS_NOTIFICACAO),
  /** ISO 8601 */
  criadoEm: z.string(),
  /** ISO 8601 — `null` enquanto pendente */
  enviadaEm: z.string().nullable(),
  /** nome/login/e-mail de quem marcou como enviada — `null` enquanto pendente */
  enviadaPor: z.string().nullable(),
  enviadaPorId: z.string().uuid().nullable(),
  origem: notificacaoOrigemSchema,
});
export type NotificacaoPainel = z.infer<typeof notificacaoPainelSchema>;

export const listarNotificacoesRespostaSchema = z.object({
  itens: z.array(notificacaoPainelSchema),
});
export type ListarNotificacoesResposta = z.infer<typeof listarNotificacoesRespostaSchema>;

/**
 * `detalhes` do 409 `conflito` quando outro operador já marcou a notificação
 * como enviada (E10) — a UI mostra "fulano já enviou".
 */
export const notificacaoConflitoDetalhesSchema = z.object({
  enviadaPor: z.string().nullable(),
  enviadaEm: z.string().nullable(),
});
export type NotificacaoConflitoDetalhes = z.infer<typeof notificacaoConflitoDetalhesSchema>;
